import { GetUpsellUrlType, UpSellProductType, UpSellVariantType } from "lib/types/upsell";
import { appendParamsToItemsUrl } from "./checkout";

type ParseUpsellDataReturnType = {
  discountedUpsells: UpSellProductType[];
  nonDiscountedUpsells: UpSellProductType[];
};

// Maps a single raw API product onto UpSellProductType (variant image resolution,
// out-of-stock filtering). Pure mapping, no error boundary of its own — callers that loop
// over a full response keep owning a single try/catch around the whole loop, matching this
// module's original error semantics (one malformed product must not abort the batch, but a
// throw here should still be caught exactly where it used to be caught).
const mapUpsellProduct = (product: any): UpSellProductType | null => {
  const variantImages: Record<string, string> = {};
  const defaultImage = Boolean(product?.images?.find((image: any) => image?.is_primary))
    ? product?.images?.find((image: any) => image?.is_primary)?.src
    : product?.images?.[0]?.src;
  product?.images?.forEach((image: any, ind: number) => {
    image?.linked_variant_ids?.forEach((variantId: string, ind: number) => {
      variantImages[variantId] = image?.src;
    });
  });
  const variants: UpSellVariantType[] = product?.variants
    ?.filter((variant: any) => !Boolean(variant?.out_of_stock))
    ?.map((variant: any, ind: number): UpSellVariantType => {
      return {
        variantId: variant?.variant_id,
        name: variant?.name,
        inventoryQuantity: variant?.inventory_quantity,
        inventoryPolicy: variant?.inventory_policy ?? "DENY",
        currentPrice: variant?.current_price,
        originalPrice: variant?.original_price,
        imageLink: variantImages[variant?.variant_id] ?? defaultImage,
        outOfStock: Boolean(variant?.out_of_stock),
        currency: variant?.currency,
        taxable: Boolean(variant?.taxable),
        requiresShipping: Boolean(variant?.requires_shipping),
        createdAt: variant?.created_at,
        updatedAt: variant?.updated_at,
        isDiscountedUpsell: Boolean(variant?.upsell_discount_applied),
        discountedPrice: variant?.upsell_discounted_price,
        description: variant?.description,
      };
    });
  if (Boolean(product?.all_variants_out_of_stock)) {
    return null;
  }
  return {
    productId: product?.product_id,
    name: product?.name,
    variants: variants,
    allVariantOutOfStock: Boolean(product?.all_variants_out_of_stock),
    upsellIds: Boolean(product?.upsell_ids?.length) ? product?.upsell_ids : [],
    createdAt: product?.created_at,
    updatedAt: product?.updated_at,
    hasComponents: Boolean(product?.has_variants_that_requires_components),
    description: product?.description,
    images: product.images.map((item: any) => item?.src),
  };
};

export const parseUpSellData = (upsellResponse: any): ParseUpsellDataReturnType => {
  if (!upsellResponse || !Boolean(upsellResponse)) {
    return {
      discountedUpsells: [],
      nonDiscountedUpsells: [],
    };
  }

  const nonDiscountedUpsells: UpSellProductType[] = [];
  const discountedUpsells: UpSellProductType[] = [];

  try {
    upsellResponse?.forEach((product: any) => {
      const upsellToPush = mapUpsellProduct(product);
      if (!upsellToPush) return;
      if (Boolean(product?.discounted_upsell_present)) {
        discountedUpsells?.push(upsellToPush);
      } else {
        nonDiscountedUpsells?.push(upsellToPush);
      }
    });
  } catch (e) {
    console.error(e);
  }

  return {
    discountedUpsells,
    nonDiscountedUpsells,
  };
};

// Recently-viewed needs the plain per-product parse in original order — parseUpSellData's
// discount bucketing would reorder products away from view-recency order.
export const parseUpsellProducts = (upsellResponse: any): UpSellProductType[] => {
  if (!upsellResponse || !Boolean(upsellResponse)) {
    return [];
  }

  const products: UpSellProductType[] = [];

  try {
    upsellResponse?.forEach((product: any) => {
      const parsedProduct = mapUpsellProduct(product);
      if (parsedProduct) {
        products.push(parsedProduct);
      }
    });
  } catch (e) {
    console.error(e);
  }

  return products;
};

export const parseUpsellIds = (upsellResponse: any): Array<string> => {
  const upsellIds: Array<string> = [];
  upsellResponse?.forEach((product: any) => {
    if (!Boolean(product?.upsell_ids?.length)) return;
    product?.upsell_ids?.forEach((upsell_id: string) => {
      if (!Boolean(upsellIds.some((upsellId: string) => upsellId === upsell_id))) {
        upsellIds.push(upsell_id);
      }
    });
  });
  return upsellIds;
};

export const parseUpsellWithProduct = (
  upsellResponse: any,
): Array<Record<string, Array<string> | string>> => {
  const upsellIds = parseUpsellIds(upsellResponse);
  const upsellWithProduct: Array<Record<string, Array<string> | string>> = [];
  upsellIds.forEach((upsell: string) => {
    upsellWithProduct.push({
      upsellId: upsell,
      products: upsellResponse
        ?.filter((product: any) => product?.upsell_ids?.includes(upsell))
        ?.map((product: any) => product?.product_id),
    });
  });
  return upsellWithProduct;
};

export const getUpsellUrl = ({
  checkoutId = null,
  tokenId = null,
}: GetUpsellUrlType): string => {
  // Extract page from current URL query params

  if (!checkoutId) {
    const UPSELL_PATH_V1 = appendParamsToItemsUrl("/upsell", {
      tokenId: tokenId ?? "",
    });
    if (tokenId) return UPSELL_PATH_V1;
  }

  const UPSELL_PATH_V2 = appendParamsToItemsUrl(`/checkout/${checkoutId}/upsell`);
  return UPSELL_PATH_V2;
};
